/**
 * API Service Layer - SECURED
 * Handles HTTP requests to the backend API with authentication
 */

import axios from 'axios';
import { auth } from '../firebase'; // Import the initialized auth instance

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

console.log('API Base URL:', API_BASE_URL);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  },
});

/**
 * Get the current user's Firebase ID token for API authentication
 * @returns {Promise<string|null>} The ID token or null if not authenticated
 */
const getAuthToken = async () => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.warn('No authenticated user found');
      return null;
    }
    
    // Get a fresh token (Firebase will cache it if still valid)
    const token = await currentUser.getIdToken(true);
    console.log('🔑 Got auth token for:', currentUser.email);
    return token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
};

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  async (config) => {
    // Get fresh auth token for each request
    const token = await getAuthToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('Making API request without authentication token');
    }
    
    console.log('Request:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const status = error.response.status;
      
      // Handle authentication errors
      if (status === 401) {
        console.error('Authentication failed - user may need to login again');
        // Optionally trigger a re-login flow here
      }
      
      // Handle authorization errors
      if (status === 403) {
        console.error('Access denied - insufficient permissions');
      }
      
      // Handle rate limiting
      if (status === 429) {
        console.error('Rate limit exceeded - please wait before retrying');
      }
      
      console.error('API Error Response:', {
        status: status,
        statusText: error.response.statusText,
        url: error.config?.url,
        data: error.response.data,
      });
    } else if (error.request) {
      console.error('API No Response:', {
        message: 'No response received',
        url: error.config?.url,
        method: error.config?.method,
      });
    } else {
      console.error('API Request Setup Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Error handler for API calls
const handleApiError = (error, operation = 'API operation') => {
  console.error(`${operation} failed:`, error.response?.data || error.message);
  return {
    data: null,
    error:
      error.response?.data?.error || error.message || 'Unknown error occurred',
  };
};

// ============================================================================
// API SERVICE - BROWSER COMPATIBLE (NO NODE.JS DEPENDENCIES)
// ============================================================================

const apiService = {
  // ============================================================================
  // USER OPERATIONS
  // ============================================================================

  async getUsers({
    search = '',
    status = '',
    limit = 10,
    offset = 0,
    orderBy = 'created_at',
    order = 'desc',
  } = {}) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());
      if (orderBy) params.append('orderBy', orderBy);
      if (order) params.append('order', order);

      const response = await apiClient.get(`/users?${params.toString()}`);

      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        pagination: response.data.pagination || {},
        error: null,
      };
    } catch (error) {
      return handleApiError(error, 'Get users');
    }
  },

  async getUserById(id) {
    try {
      const response = await apiClient.get(`/users/${id}`);
      return response.data.data || null;
    } catch (error) {
      console.error('Get user by ID failed:', error);
      return null;
    }
  },

  async createUser(userData) {
    try {
      const response = await apiClient.post('/users', userData);
      return response.data.data || null;
    } catch (error) {
      console.error('Create user failed:', error);
      throw error;
    }
  },

  async updateUser(id, updates) {
    try {
      const response = await apiClient.put(`/users/${id}`, updates);
      return response.data.data || null;
    } catch (error) {
      console.error('Update user failed:', error);
      throw error;
    }
  },

  async deleteUser(id) {
    try {
      const response = await apiClient.delete(`/users/${id}`);
      return response.data.success || false;
    } catch (error) {
      console.error('Delete user failed:', error);
      throw error;
    }
  },

  // ============================================================================
  // TRANSACTION OPERATIONS
  // ============================================================================

  async addTransaction(transactionData) {
    try {
      const response = await apiClient.post('/transactions', transactionData);
      return response.data.data || null;
    } catch (error) {
      console.error('Add transaction failed:', error);
      throw error;
    }
  },

  async getUserTransactions(userId) {
    try {
      const response = await apiClient.get(`/transactions/user/${userId}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Get user transactions failed:', error);
      return [];
    }
  },

  // ============================================================================
  // DIVIDEND OPERATIONS
  // ============================================================================

  async checkDividendsExist(year) {
    try {
      const response = await apiClient.get(`/dividends/exists/${year}`);
      return {
        exists: response.data.exists || false,
        count: response.data.count || 0,
      };
    } catch (error) {
      console.error('Check dividends exist failed:', error);
      return { exists: false, count: 0 };
    }
  },

  async processDividends(year, multiplier = 0.09384, processedBy = 'system') {
    try {
      const response = await apiClient.post('/dividends/process', {
        year: year,
        multiplier: multiplier,
        processed_by: processedBy,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Process dividends failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  async getDividendsByYear(year) {
    try {
      const response = await apiClient.get(`/dividends/year/${year}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Get dividends by year failed:', error);
      return [];
    }
  },

  async getDividendSummary(year) {
    try {
      const response = await apiClient.get(`/dividends/summary/${year}`);
      return response.data.data || null;
    } catch (error) {
      console.error('Get dividend summary failed:', error);
      return null;
    }
  },

  // ============================================================================
  // STAFF OPERATIONS
  // ============================================================================

  async getStaff() {
    try {
      const response = await apiClient.get('/staff');
      return response.data.data || [];
    } catch (error) {
      console.error('Get staff failed:', error);
      return [];
    }
  },

  async getStaffById(id) {
    try {
      const response = await apiClient.get(`/staff/${id}`);
      return response.data.data || null;
    } catch (error) {
      // Return null for staff not found (common case)
      return null;
    }
  },

  // Create a complete staff account (super-user only). The backend creates
  // the Firebase Auth account, Firestore role doc and Postgres row together.
  async createStaff({ name, username, email, password, role }) {
    const response = await apiClient.post('/staff', {
      name,
      username,
      email,
      password,
      role,
    });
    return response.data.data || null;
  },

  // ============================================================================
  // SYSTEM OPERATIONS
  // ============================================================================

  async getSystemMetadata(key) {
    try {
      const response = await apiClient.get(`/metadata/${key}`);
      return response.data.data || null;
    } catch (error) {
      console.error('Get system metadata failed:', error);
      return null;
    }
  },

  async updateSystemMetadata(key, value, updatedBy) {
    try {
      const response = await apiClient.put(`/metadata/${key}`, {
        value: value,
        updated_by: updatedBy,
      });
      return response.data.success || false;
    } catch (error) {
      console.error('Update system metadata failed:', error);
      throw error;
    }
  },

  // ============================================================================
  // CERTIFICATE OPERATIONS
  // ============================================================================

  async getCertificates({
    search = '',
    status = '',
    limit = 20,
    offset = 0,
  } = {}) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const response = await apiClient.get(`/certificates?${params.toString()}`);
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        error: null,
      };
    } catch (error) {
      return handleApiError(error, 'Get certificates');
    }
  },

  async getUserCertificates(userId) {
    try {
      const response = await apiClient.get(`/certificates/user/${userId}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Get user certificates failed:', error);
      return [];
    }
  },

  async getCertificateById(id) {
    try {
      const response = await apiClient.get(`/certificates/${id}`);
      return response.data.data || null;
    } catch (error) {
      console.error('Get certificate by ID failed:', error);
      return null;
    }
  },

  async issueCertificate(certData) {
    try {
      const response = await apiClient.post('/certificates', certData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Issue certificate failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  // Bulk-issue certificates to every shareholder with shares but no active
  // certificate. Long-running; uses an extended timeout.
  async bulkIssueCertificates() {
    try {
      const response = await apiClient.post('/certificates/bulk', {}, { timeout: 120000 });
      return {
        success: true,
        message: response.data.message,
        issued_count: response.data.issued_count,
        eligible_total: response.data.eligible_total,
        already_active: response.data.already_active,
      };
    } catch (error) {
      console.error('Bulk issue certificates failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  async revokeCertificate(id, reason) {
    try {
      const response = await apiClient.put(`/certificates/${id}/revoke`, { reason });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Revoke certificate failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async testConnection() {
    try {
      const response = await apiClient.get('/health');
      console.log('✅ API connection test:', response.data.message);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ API connection test failed:', error.message);
      return { success: false, error: error.message };
    }
  },
};

// Export both named and default exports for compatibility
export { apiService, getAuthToken };
export default apiService;
